import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { FlaskConical, LogOut, ShieldAlert } from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import { isTestMode, setTestMode } from '../../lib/testMode.js';
import { client, clearImpersonationToken } from '../../main.js';
import { Topbar } from './Topbar.js';
import { WelcomeGate } from '../welcome/WelcomeModal.js';

// Where to send the user back after exiting impersonation. Same env-var
// pattern as VITE_MANAGER_URL on the admin side.
const ADMIN_URL =
  (import.meta.env.VITE_ADMIN_URL as string | undefined) ??
  'http://localhost:5174';

// Page-level banners (e.g. unsaved-changes nudges) portal into this slot so
// they sit just below the topbar, full-bleed across the viewport, and
// outside the page's max-w-[1400px] content column.
const BannerSlotContext = createContext<HTMLDivElement | null>(null);

export function useBannerSlot(): HTMLDivElement | null {
  return useContext(BannerSlotContext);
}

// Observed rendered height of an element, for driving sticky offsets.
function useMeasuredHeight(el: HTMLElement | null): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!el) return;
    const update = () => setHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return height;
}

export function AppShell({ children }: { children: ReactNode }) {
  // Two measured heights drive every sticky offset below, exposed as CSS
  // variables on the shell root so consumers can read them without prop
  // drilling:
  // - `--header-h`: the sticky header block (impersonation/test-mode
  //   banners + topbar). Falls back to the bare topbar height (4rem)
  //   until measured.
  // - `--banner-h`: the page-banner slot, so sticky elements further down
  //   (e.g. the proptree preview) offset by
  //   `calc(var(--header-h, 4rem) + var(--banner-h, 0px))` and never get
  //   overlapped.
  const [header, setHeader] = useState<HTMLDivElement | null>(null);
  const headerHeight = useMeasuredHeight(header);
  const [bannerSlot, setBannerSlot] = useState<HTMLDivElement | null>(null);
  const bannerHeight = useMeasuredHeight(bannerSlot);

  return (
    <BannerSlotContext.Provider value={bannerSlot}>
      <div
        className="flex min-h-screen flex-col bg-background"
        style={{
          ['--banner-h' as string]: `${bannerHeight}px`,
          ['--header-h' as string]: `${headerHeight || 64}px`,
        }}
      >
        {/* One sticky block so the banners stay visible above the top
            menu while scrolling instead of scrolling away. */}
        <div ref={setHeader} className="sticky top-0 z-30">
          <ImpersonationBanner />
          <TestModeBanner />
          <Topbar />
        </div>
        <div
          ref={setBannerSlot}
          className="sticky top-[var(--header-h,4rem)] z-20"
        />
        <main className="flex flex-1 flex-col">
          <div className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col px-6 py-8">
            {children}
          </div>
        </main>
        <WelcomeGate />
      </div>
    </BannerSlotContext.Provider>
  );
}

// Amber strip shown while the profile-dropdown "Testing" switch is on —
// every registered list/stat endpoint is served from seed fixtures, so
// the user needs a constant reminder that nothing on screen is real.
function TestModeBanner() {
  if (!isTestMode()) return null;
  return (
    <div className="border-b border-amber-300/70 bg-amber-100 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2 text-[13px]">
        <FlaskConical className="h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 truncate">
          You are in <strong className="font-semibold">test mode</strong> —
          the data shown is fake seed data.
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md border border-amber-400/60 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors hover:bg-amber-200/60 dark:border-amber-500/40 dark:hover:bg-amber-900/40"
          onClick={() => {
            setTestMode(false);
            window.location.reload();
          }}
        >
          Exit
        </button>
      </div>
    </div>
  );
}

// Sticky strip at the top of every authenticated manager page, visible only
// when the active session was minted via /api/auth/admin/login-as. Shows
// who the admin really is and offers a one-click "exit" that re-issues the
// admin cookie and bounces back to the admin portal.
function ImpersonationBanner() {
  const { user, impersonator } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!user || !impersonator) return null;

  const managerLabel = user.name ?? user.email;
  const adminLabel = impersonator.name ?? impersonator.email;

  const handleExit = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      // Re-issues the admin cookie on the API side. The cookie domain is
      // shared with the admin portal in dev (localhost) and via
      // COOKIE_DOMAIN in prod, so the next admin request picks it up.
      await client.auth.exitImpersonation();
      clearImpersonationToken();
      window.location.href = ADMIN_URL;
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-amber-300/70 bg-amber-100 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2 text-[13px]">
        <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 truncate">
          Logged in as <strong className="font-semibold">{managerLabel}</strong>
          <span className="mx-2 opacity-60">·</span>
          originally{' '}
          <strong className="font-semibold">{adminLabel}</strong>
          {err && (
            <span className="ml-3 font-mono text-[11px] text-destructive">
              {err}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleExit}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-amber-400/70 bg-white/70 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.04em] text-amber-950 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-500/40 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
        >
          <LogOut className="h-3.5 w-3.5" />
          {busy ? 'Exiting…' : 'Exit impersonation'}
        </button>
      </div>
    </div>
  );
}
